/**
 * OpportunityPlanCard.tsx
 *
 * Inline expandable card for a single Opportunity inside AccountPlanTab.
 * Shows opp details, decision criteria, blockers, competition, next step,
 * per-opp composite score, and close/lost/no-decision footer.
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7
 * Roles: bdr, ae, manager, executive, marketing — NEVER "rep"
 * M-PACT always spelled with hyphen
 */

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Plus, X, CheckCircle2, XCircle, Minus } from "lucide-react";
import type { Opportunity, MpactBlock } from "@/types/account-plan";
import { TimingDefenseGate } from "@/components/cp10/TimingDefenseGate";
import { getHealthColor } from "@shared/schema";

const BLACK = "#0B0B0F";
const RED  = "#DC2626";
const BG   = "#FAFAF7";

// Stage display labels
// D-091 (2026-05-22): Realigned to canon (Project, Need, Pain, Fit, Budget,
// Timing, Authority, Org, Competition, Final Barrier). Storage keys retained.
const STAGE_LABELS: Record<string, string> = {
  cp1_project:      "CP1 · Project",
  cp2_pain:         "CP2 · Need / Pain / Demonstration",
  cp3_demonstration:"CP3 · Budget",
  cp4_budget:       "CP4 · Timeline",
  cp5_timeline:     "CP5 · Authority — Project / Initiative",
  cp6_authority:    "CP6 · Technical and Financial Sign-off",
  cp7_people:       "CP7 · Org Structure and Influence Map",
  cp8_competition:  "CP8 · Competitive Position",
  cp9_finance:      "CP9 · Compliance — IT Internal and Industry-Specific",
  cp10_legal:       "CP10 · Legal — Contracts, SLAs, Terms",
  closed_won:       "Closed Won",
  closed_lost:      "Closed Lost",
  disqualified:     "Disqualified",
};

const STAGE_COLOR: Record<string, string> = {
  cp1_project: "#8E8E94",
  cp2_pain: "#8E8E94",
  cp3_demonstration: "#EA580C",
  cp4_budget: "#EA580C",
  cp5_timeline: "#EA580C",
  cp6_authority: "#525258",
  cp7_people: "#525258",
  cp8_competition: "#DC2626",
  cp9_finance: "#16A34A",
  cp10_legal: "#16A34A",
  closed_won: "#16A34A",
  closed_lost: "#DC2626",
  disqualified: "#DC2626",
};

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

// Per-opp composite is score-driven — canon palette per CANON.md §5.
function scoreColor(score: number): string {
  return getHealthColor(score);
}

/** Compute a rough per-opp score from M-PACT block answered fields */
function computeOppScore(mpact: MpactBlock, winProbability: number): number {
  const cpKeys: (keyof MpactBlock)[] = [
    "cp1_project", "cp2_pain", "cp3_demonstration", "cp4_budget",
    "cp5_timeline", "cp6_authority", "cp7_people", "cp8_competition",
    "cp9_finance", "cp10_legal",
  ];
  let answered = 0;
  for (const k of cpKeys) {
    const field = mpact[k] as any;
    if (field?.answered) answered++;
  }
  const coverage = (answered / cpKeys.length) * 50;
  const prob = (winProbability / 100) * 50;
  return Math.round(coverage + prob);
}

interface Props {
  opp: Opportunity;
  mpact: MpactBlock;
  accountId: number; // parent account bigint id, threaded down for the timing gate
  onUpdate: (updated: Opportunity) => void;
  onClose?: () => void;
  isNew?: boolean;
}

export function OpportunityPlanCard({ opp, mpact, accountId, onUpdate, onClose, isNew = false }: Props) {
  const [expanded, setExpanded] = useState(isNew);
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState<Opportunity>({ ...opp });
  const [newCriteria, setNewCriteria] = useState("");
  const [newBlocker, setNewBlocker] = useState("");
  const [closeModal, setCloseModal] = useState<"won" | "lost" | "no_decision" | null>(null);
  const [closeReason, setCloseReason] = useState("");

  // S-CP10-4: Timing Defense Gate fires when the close date is changed on a CP10 opp.
  const [timingGateOpen, setTimingGateOpen] = useState(false);
  const [pendingCloseDate, setPendingCloseDate] = useState<string | null>(null);

  function handleCloseDateChange(newValue: string) {
    const prev = (draft.expected_close || "").slice(0, 10);
    setDraft(d => ({ ...d, expected_close: newValue }));
    const isCp10 = draft.stage === "cp10_legal";
    if (isCp10 && newValue && newValue !== prev) {
      setPendingCloseDate(newValue);
      setTimingGateOpen(true);
    }
  }

  // Esc key dismissal for close-reason modal
  useEffect(() => {
    if (!closeModal) return;
    function handleEsc(e: KeyboardEvent) { if (e.key === "Escape") setCloseModal(null); }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [closeModal]);

  const oppScore = computeOppScore(mpact, opp.win_probability);
  const stageColor = STAGE_COLOR[opp.stage] || "#8E8E94";

  function saveEdits() {
    onUpdate({ ...draft, updated_at: new Date().toISOString() });
    setEditing(false);
    setExpanded(true);
  }

  function handleClose() {
    if (!closeModal || !closeReason.trim()) return;
    const stageMap = {
      won: "closed_won" as const,
      lost: "closed_lost" as const,
      no_decision: "disqualified" as const,
    };
    onUpdate({
      ...opp,
      stage: stageMap[closeModal],
      updated_at: new Date().toISOString(),
    });
    setCloseModal(null);
    setCloseReason("");
  }

  function addCriteria() {
    if (!newCriteria.trim()) return;
    setDraft(d => ({ ...d, decision_criteria: [...d.decision_criteria, newCriteria.trim()] }));
    setNewCriteria("");
  }

  function removeCriteria(i: number) {
    setDraft(d => ({ ...d, decision_criteria: d.decision_criteria.filter((_, idx) => idx !== i) }));
  }

  function addBlocker() {
    if (!newBlocker.trim()) return;
    setDraft(d => ({ ...d, blockers: [...d.blockers, newBlocker.trim()] }));
    setNewBlocker("");
  }

  function removeBlocker(i: number) {
    setDraft(d => ({ ...d, blockers: d.blockers.filter((_, idx) => idx !== i) }));
  }

  const isClosed = opp.stage === "closed_won" || opp.stage === "closed_lost" || opp.stage === "disqualified";

  return (
    <div
      style={{
        border: `1px solid ${isClosed ? "#E5E5E7" : "#C9C9CC"}`,
        borderRadius: 8,
        background: "#fff",
        marginBottom: 12,
        opacity: isClosed ? 0.7 : 1,
      }}
    >
      {/* Header row */}
      <div
        className="flex items-center justify-between cursor-pointer px-4 py-3 gap-3"
        onClick={() => !editing && setExpanded(e => !e)}
        style={{ borderBottom: expanded ? "1px solid #E5E5E7" : "none" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ background: stageColor + "20", color: stageColor, flexShrink: 0 }}
          >
            {STAGE_LABELS[opp.stage] || opp.stage}
          </span>
          <span className="font-medium text-sm truncate" style={{ color: BLACK }}>{opp.name}</span>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="hidden sm:flex items-baseline gap-1">
            <span className="text-sm font-semibold tabular" style={{ color: BLACK }}>{fmt(opp.amount)}</span>
            <span className="text-[11px] text-neutral-500">· {opp.win_probability}% prob</span>
          </div>
          {/* Opp score badge */}
          <div
            className="text-[11px] font-semibold tabular px-2 py-0.5 rounded"
            style={{ background: scoreColor(oppScore) + "18", color: scoreColor(oppScore) }}
          >
            {oppScore}
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-500" />
          )}
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-4 py-4 space-y-5">
          {/* Composite score widget */}
          <div
            className="rounded p-3 flex items-center gap-4"
            style={{ background: BG, border: "1px solid #E5E5E7" }}
          >
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">Opp composite</div>
              <div
                className="text-3xl font-semibold tabular mt-0.5"
                style={{ color: scoreColor(oppScore) }}
              >
                {oppScore}
              </div>
            </div>
            <div className="flex-1">
              <div className="h-2 rounded bg-neutral-100 overflow-hidden">
                <div
                  className="h-full rounded transition-all"
                  style={{ width: `${oppScore}%`, background: scoreColor(oppScore) }}
                />
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">
                Composite coverage + win probability weighted
              </div>
            </div>
          </div>

          {editing ? (
            /* ---- Edit mode ---- */
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500">Opp name</label>
                  <input
                    className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400"
                    value={draft.name}
                    onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500">Stage</label>
                  <select
                    className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                    value={draft.stage}
                    onChange={e => setDraft(d => ({ ...d, stage: e.target.value as any }))}
                  >
                    {Object.entries(STAGE_LABELS).filter(([k]) => !["closed_won","closed_lost","disqualified"].includes(k)).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500">Amount (USD)</label>
                  <input
                    type="number"
                    className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                    value={draft.amount}
                    onChange={e => setDraft(d => ({ ...d, amount: Number(e.target.value) }))}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500">Win probability (%)</label>
                  <input
                    type="number"
                    min={0} max={100}
                    className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                    value={draft.win_probability}
                    onChange={e => setDraft(d => ({ ...d, win_probability: Number(e.target.value) }))}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500">Expected close</label>
                  <input
                    type="date"
                    className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                    value={draft.expected_close?.slice(0, 10) || ""}
                    onChange={e => handleCloseDateChange(e.target.value)}
                    data-testid="opp-expected-close"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500">Next step date</label>
                  <input
                    type="date"
                    className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                    value={draft.next_step_date?.slice(0, 10) || ""}
                    onChange={e => setDraft(d => ({ ...d, next_step_date: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500">Next step</label>
                <input
                  className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                  value={draft.next_step}
                  onChange={e => setDraft(d => ({ ...d, next_step: e.target.value }))}
                  placeholder="What is the next concrete step?"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500">Competition</label>
                <input
                  className="w-full mt-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none"
                  value={draft.competition}
                  onChange={e => setDraft(d => ({ ...d, competition: e.target.value }))}
                  placeholder="Known competitors or status quo"
                />
              </div>

              {/* Decision criteria */}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500">Decision criteria</label>
                <div className="mt-1 space-y-1">
                  {draft.decision_criteria.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="flex-1 text-sm px-3 py-1.5 rounded bg-neutral-50 border border-neutral-100">{c}</span>
                      <button onClick={() => removeCriteria(i)} className="text-neutral-500 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-1">
                    <input
                      className="flex-1 px-3 py-1.5 rounded border border-neutral-200 text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400"
                      value={newCriteria}
                      onChange={e => setNewCriteria(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && addCriteria()}
                      placeholder="Add a decision criterion..."
                    />
                    <button
                      onClick={addCriteria}
                      className="px-3 py-1.5 rounded text-sm font-medium"
                      style={{ background: BLACK, color: "#fff" }}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Blockers */}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-neutral-500">Blockers</label>
                <div className="mt-1 space-y-1">
                  {draft.blockers.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="flex-1 text-sm px-3 py-1.5 rounded bg-red-50 border border-red-100 text-red-800">{b}</span>
                      <button onClick={() => removeBlocker(i)} className="text-neutral-500 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-1">
                    <input
                      className="flex-1 px-3 py-1.5 rounded border border-neutral-200 text-sm focus:outline-none focus:ring-1 focus:ring-neutral-400"
                      value={newBlocker}
                      onChange={e => setNewBlocker(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && addBlocker()}
                      placeholder="Add a blocker..."
                    />
                    <button
                      onClick={addBlocker}
                      className="px-3 py-1.5 rounded text-sm font-medium"
                      style={{ background: RED, color: "#fff" }}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t border-neutral-100">
                <button
                  onClick={saveEdits}
                  className="px-4 py-2 rounded text-sm font-medium"
                  style={{ background: BLACK, color: "#fff" }}
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditing(false); setDraft({ ...opp }); if (isNew && onClose) onClose(); }}
                  className="px-4 py-2 rounded text-sm font-medium border border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* ---- Read mode ---- */
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Amount" value={fmt(opp.amount)} />
                <Stat label="Win probability" value={`${opp.win_probability}%`} />
                <Stat label="Expected close" value={opp.expected_close ? new Date(opp.expected_close).toLocaleDateString() : "—"} />
                <Stat label="Forecast bucket" value={opp.forecast_bucket || "—"} />
              </div>

              {opp.next_step && (
                <div className="rounded p-3" style={{ background: BG, border: "1px solid #E5E5E7" }}>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Next step</div>
                  <p className="text-sm" style={{ color: BLACK }}>{opp.next_step}</p>
                  {opp.next_step_date && (
                    <p className="text-[11px] text-neutral-500 mt-1">Due {new Date(opp.next_step_date).toLocaleDateString()}</p>
                  )}
                </div>
              )}

              {opp.competition && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Competition</div>
                  <p className="text-sm text-neutral-700">{opp.competition}</p>
                </div>
              )}

              {opp.decision_criteria?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Decision criteria</div>
                  <ul className="space-y-1">
                    {opp.decision_criteria.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: BLACK }} />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {opp.blockers?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Blockers</div>
                  <ul className="space-y-1">
                    {opp.blockers.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: RED }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!isClosed && (
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={() => setEditing(true)}
                    className="px-3 py-1.5 rounded text-xs font-medium border border-neutral-200 hover:bg-neutral-50"
                    style={{ color: BLACK }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Close / outcome footer */}
          {!isClosed && !editing && (
            <div
              className="flex items-center gap-3 pt-3 border-t"
              style={{ borderColor: "#E5E5E7" }}
            >
              <span className="text-[11px] uppercase tracking-wider text-neutral-500 mr-1">Outcome</span>
              <button
                onClick={() => setCloseModal("won")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: "rgba(22,163,74,0.14)", color: "#16A34A" }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark won
              </button>
              <button
                onClick={() => setCloseModal("lost")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: "rgba(220,38,38,0.12)", color: "#DC2626" }}
              >
                <XCircle className="w-3.5 h-3.5" /> Mark lost
              </button>
              <button
                onClick={() => setCloseModal("no_decision")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: "#EFEFEE", color: "#6B6B70" }}
              >
                <Minus className="w-3.5 h-3.5" /> No decision
              </button>
            </div>
          )}

          {isClosed && (
            <div className="text-xs text-neutral-500 pt-2 border-t border-neutral-100">
              Closed as <strong>{STAGE_LABELS[opp.stage]}</strong>
            </div>
          )}
        </div>
      )}

      {/* Close reason modal */}
      {closeModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.5)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setCloseModal(null); }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="rounded-xl p-6 w-full max-w-md mx-4"
            style={{ background: "#fff" }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-base font-semibold" style={{ color: BLACK }}>
                {closeModal === "won" ? "Mark as Won" : closeModal === "lost" ? "Mark as Lost" : "No Decision"}
              </h3>
              <button
                type="button"
                onClick={() => setCloseModal(null)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors flex-shrink-0 ml-2"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
            <p className="text-sm text-neutral-500 mb-4">
              A reason is required before closing this opportunity.
            </p>
            <textarea
              className="w-full px-3 py-2 rounded border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-neutral-400"
              rows={3}
              placeholder="Enter the reason..."
              value={closeReason}
              onChange={e => setCloseReason(e.target.value)}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleClose}
                disabled={!closeReason.trim()}
                className="flex-1 py-2 rounded text-sm font-medium disabled:opacity-50"
                style={{ background: BLACK, color: "#fff" }}
              >
                Confirm
              </button>
              <button
                onClick={() => setCloseModal(null)}
                className="flex-1 py-2 rounded text-sm font-medium border border-neutral-200 text-neutral-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {timingGateOpen && pendingCloseDate && (
        <TimingDefenseGate
          open={timingGateOpen}
          dealId={opp.id}
          accountId={accountId}
          closeDate={pendingCloseDate}
          compositePct={computeOppScore(mpact, draft.win_probability)}
          onClose={() => setTimingGateOpen(false)}
          onComplete={({ blendedForecastPct, declaredButton }) => {
            // Persisted by the gate; expose blended forecast on the draft for visibility.
            console.info("[OpportunityPlanCard] timing gate complete", {
              dealId: opp.id,
              declaredButton,
              blendedForecastPct,
            });
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded p-2.5" style={{ background: "#FAFAF7", border: "1px solid #E5E5E7" }}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm font-medium mt-0.5" style={{ color: "#0B0B0F" }}>{value}</div>
    </div>
  );
}
